import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { useListFrom } from '../../hooks/useUrlParam';
import { cornerName } from '../../utils/personName';
import { STAGE_PILL_CLS } from '../../utils/tournament';
import type { Bout } from '../../types';

/** The live bouts of a tournament: a pulse dot, the ring, the red boxer
 *  against the blue boxer, and the stage. The tournament card and the
 *  Overview tab share this markup (#661). Before it, only the Overview copy
 *  had the phone layout of #552.
 *
 *  ONE grid holds all rows and each row is a subgrid, so "vs" has the same
 *  position in every row and each column is only as wide as its widest cell.
 *  On a phone the stage pill goes under the first cell, so the names get the
 *  width, and a long name wraps (never `truncate`, #552).
 *
 *  The two views differ on purpose:
 *  - `overview` shows the bout number, and the pills are 10 px. It draws its
 *    own card: the heading above it already says "Live".
 *  - `card` shows the word "Live" in place of the number, the pills are 12 px,
 *    and the rows sit under the main link of the tournament card. */
export default function LiveBoutRows({ bouts, variant }: { bouts: Bout[]; variant: 'card' | 'overview' }) {
  const { t } = useI18n();
  const { stageLabel } = useLabels();
  // #595 — the bout page goes back to this list with its position.
  const listFrom = useListFrom();
  const overview = variant === 'overview';
  const pillSize = overview ? 'text-[10px]' : 'text-xs';

  return (
    <div className={`grid gap-x-2 grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)_auto] ${overview ? 'card overflow-hidden' : ''}`}>
      {bouts.map(bout => {
        const stageCls = bout.stage ? STAGE_PILL_CLS[bout.stage] : undefined;
        return (
          <Link key={bout.id} to={`/bouts/${bout.id}`} state={listFrom}
            className={`col-span-full grid grid-cols-subgrid gap-x-2 gap-y-0.5 items-center px-3 py-1.5 bg-green-50 border-t border-green-100 ${overview ? 'first:border-t-0' : ''} text-xs font-medium text-green-700 hover:bg-green-100 transition-colors`}>
            <span className={`col-start-1 row-start-1 flex items-center gap-1.5 ${stageCls ? '' : 'max-sm:row-span-2'}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              {/* The bout number (operator, 2026-09-29): the heading of the
                  Overview already says Live, and the pulsing dot marks it. */}
              {overview
                ? <span className="font-semibold whitespace-nowrap">#{bout.bout_number}</span>
                : <span className="whitespace-nowrap">{t.common.live}</span>}
              {bout.ring_name && (
                <span className={`inline-flex items-center px-1 py-0 rounded-sm bg-green-100 text-green-700 whitespace-nowrap ${pillSize}`}>
                  {bout.ring_name}
                </span>
              )}
            </span>
            <span className="col-start-2 row-start-1 max-sm:row-span-2 text-red-500 text-right leading-tight break-words">{cornerName(bout, 'red') || '?'}</span>
            <span className="col-start-3 row-start-1 max-sm:row-span-2 text-gray-400 font-normal">vs</span>
            <span className="col-start-4 row-start-1 max-sm:row-span-2 text-blue-500 leading-tight break-words">{cornerName(bout, 'blue') || '?'}</span>
            {stageCls && bout.stage && (
              <span className={`max-sm:col-start-1 max-sm:row-start-2 sm:col-start-5 sm:row-start-1 justify-self-start sm:justify-self-end inline-flex items-center px-1 py-0 rounded-sm whitespace-nowrap ${pillSize} ${stageCls}`}>
                {stageLabel(bout.stage)}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
